import {
  Point2D,
  CourbeBezier,
  LigneDroite,
  OutilDessin,
  EtatSelection,
  ProfilBaleinePersonnalise,
  ConfigurationCanvas2D,
  EvenementCanvas
} from '../types';

/**
 * Service Canvas2D pour l'outil de dessin personnalisé des baleines
 * Conforme au cahier des charges - Module 4: Tâche 4.4
 * 
 * FONCTIONNALITÉS:
 * - Dessin libre (crayon)
 * - Lignes droites
 * - Courbes de Bézier
 * - Sélection et modification
 * - Sauvegarde du profil personnalisé
 * - Interface intuitive
 */
export class Canvas2DService {
  private canvas: HTMLCanvasElement | null = null;
  private contexte: CanvasRenderingContext2D | null = null;
  private configuration: ConfigurationCanvas2D;
  private outilActuel: OutilDessin = OutilDessin.CRAYON;
  private estEnTrain: boolean = false;
  private pointDebut: Point2D | null = null;
  private pointsTemporaires: Point2D[] = [];
  private etatSelection: EtatSelection = { actif: false };
  private showGrid: boolean = true;
  private accrocheActive: boolean = true;
  private rayonAccroche: number = 20;
  private selectedPathIndex: number = -1;
  
  // Données du profil
  private lignesCrayon: Point2D[][] = [];
  private lignesDroites: LigneDroite[] = [];
  private courbesBezier: CourbeBezier[] = [];
  
  // Historique pour annuler/refaire
  private historique: ProfilBaleinePersonnalise[] = [];
  private indexHistorique: number = -1;
  
  // Callbacks
  private onChangement?: (profil: ProfilBaleinePersonnalise) => void;
  private onEvenement?: (evenement: EvenementCanvas) => void;

  constructor(configuration: ConfigurationCanvas2D) {
    this.configuration = configuration;
  }

  /**
   * Initialise le service avec un canvas
   */
  public initialiser(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.contexte = canvas.getContext('2d');
    
    if (!this.contexte) {
      throw new Error('Impossible d\'obtenir le contexte 2D du canvas');
    }
    
    // Configurer le canvas
    this.canvas.width = this.configuration.largeur;
    this.canvas.height = this.configuration.hauteur;
    
    // Ajouter les écouteurs d'événements
    this.ajouterEcouteurs();
    
    // Dessiner l'état initial
    this.redessiner();
    
    // Sauvegarder l'état initial
    this.sauvegarderEtat();
  }

  /**
   * Définit l'outil de dessin actuel
   */
  public definirOutil(outil: OutilDessin): void {
    this.outilActuel = outil;
    this.annulerSelection();
    this.emettrEvenement({ type: 'outil_change', outil });
  }

  /**
   * Obtient l'outil actuel
   */
  public obtenirOutil(): OutilDessin {
    return this.outilActuel;
  }

  /**
   * Définit le callback de changement
   */
  public definirCallbackChangement(callback: (profil: ProfilBaleinePersonnalise) => void): void {
    this.onChangement = callback;
  }

  /**
   * Définit le callback d'événement
   */
  public definirCallbackEvenement(callback: (evenement: EvenementCanvas) => void): void {
    this.onEvenement = callback;
  }

  /**
   * Charge un profil existant
   */
  public chargerProfil(profil: ProfilBaleinePersonnalise): void {
    this.lignesCrayon = [...profil.lignes];
    this.lignesDroites = [...profil.lignesDroites];
    this.courbesBezier = [...profil.courbes];
    
    this.redessiner();
    this.sauvegarderEtat();
    this.notifierChangement();
  }

  /**
   * Obtient le profil actuel
   */
  public exporterMotif(nom: string): ProfilBaleinePersonnalise {
    return this.obtenirProfil();
  }

  public importerMotif(profil: ProfilBaleinePersonnalise): void {
    this.chargerProfil(profil);
  }

  public supprimerSelection(): void {
    if (this.etatSelection.typeElement === 'ligne') {
      this.lignesDroites = this.lignesDroites.filter(l => l.id !== this.etatSelection.elementSelectionne);
    } else if (this.etatSelection.typeElement === 'bezier') {
      this.courbesBezier = this.courbesBezier.filter(c => c.id !== this.etatSelection.elementSelectionne);
    } else if (this.selectedPathIndex !== -1) {
      this.lignesCrayon.splice(this.selectedPathIndex, 1);
      this.selectedPathIndex = -1;
    }
    this.etatSelection = { actif: false };
    this.redessiner();
    this.sauvegarderEtat();
  }

  public toggleGrille(): void {
    this.showGrid = !this.showGrid;
    this.redessiner();
  }

  public toggleAccroche(): void {
    this.accrocheActive = !this.accrocheActive;
  }

  public getAccrocheActive(): boolean {
    return this.accrocheActive;
  }

  public static genererMiniature(profil: ProfilBaleinePersonnalise, taille: number): string {
    // Calculer la boîte englobante réelle du dessin (pas les dimensions du canvas)
    const tousLesPoints: Point2D[] = [];
    
    // Collecter tous les points du dessin
    profil.lignes.forEach(ligne => tousLesPoints.push(...ligne));
    profil.lignesDroites.forEach(ligne => {
      tousLesPoints.push(ligne.debut, ligne.fin);
    });
    profil.courbes.forEach(courbe => {
      tousLesPoints.push(courbe.debut, courbe.controle1, courbe.controle2, courbe.fin);
    });
    
    if (tousLesPoints.length === 0) {
      // Si aucun point, créer une miniature vide
      const canvas = document.createElement('canvas');
      canvas.width = taille;
      canvas.height = taille;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      return canvas.toDataURL();
    }
    
    // Calculer la vraie boîte englobante du dessin
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    tousLesPoints.forEach(point => {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    });
    
    const largeurDessin = maxX - minX;
    const hauteurDessin = maxY - minY;
    
    // Créer le canvas de miniature avec les proportions du dessin réel
    const canvas = document.createElement('canvas');
    canvas.width = taille;
    canvas.height = Math.max(1, Math.round(taille * (hauteurDessin / largeurDessin)));
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    // Fond
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Transformation pour adapter aux dimensions réelles du dessin
    const scaleX = canvas.width / largeurDessin;
    const scaleY = canvas.height / hauteurDessin;

    ctx.strokeStyle = '#222';
    ctx.lineWidth = 1;

    // Dessiner chemins crayon en ajustant pour la boîte englobante
    for (const ligne of profil.lignes || []) {
      if (!ligne || ligne.length === 0) continue;
      ctx.beginPath();
      for (let i = 0; i < ligne.length; i++) {
        const p = ligne[i];
        const x = (p.x - minX) * scaleX;
        const y = (p.y - minY) * scaleY;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    // Dessiner lignes droites en ajustant pour la boîte englobante
    for (const l of profil.lignesDroites || []) {
      if (!l) continue;
      ctx.beginPath();
      ctx.moveTo((l.debut.x - minX) * scaleX, (l.debut.y - minY) * scaleY);
      ctx.lineTo((l.fin.x - minX) * scaleX, (l.fin.y - minY) * scaleY);
      ctx.stroke();
    }

    // Dessiner courbes de Bézier en ajustant pour la boîte englobante
    for (const c of profil.courbes || []) {
      if (!c) continue;
      ctx.beginPath();
      ctx.moveTo((c.debut.x - minX) * scaleX, (c.debut.y - minY) * scaleY);
      ctx.bezierCurveTo(
        (c.controle1.x - minX) * scaleX, (c.controle1.y - minY) * scaleY,
        (c.controle2.x - minX) * scaleX, (c.controle2.y - minY) * scaleY,
        (c.fin.x - minX) * scaleX, (c.fin.y - minY) * scaleY
      );
      ctx.stroke();
    }

    return canvas.toDataURL();
  }

  public obtenirProfil(): ProfilBaleinePersonnalise {
    // Calculer les dimensions du profil - CORRECTION: utiliser seulement la boîte englobante du dessin
    const tousLesPoints: Point2D[] = [];
    
    // Ajouter tous les points des lignes
    this.lignesCrayon.forEach(ligne => tousLesPoints.push(...ligne));
    this.lignesDroites.forEach(ligne => {
      tousLesPoints.push(ligne.debut, ligne.fin);
    });
    this.courbesBezier.forEach(courbe => {
      tousLesPoints.push(courbe.debut, courbe.controle1, courbe.controle2, courbe.fin);
    });
    
    // Calculer les dimensions RÉELLES du dessin (pas du canvas)
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    tousLesPoints.forEach(point => {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    });
    
    // Utiliser les dimensions du dessin réel, pas du canvas
    const largeur = tousLesPoints.length > 0 ? maxX - minX : 0;
    const hauteur = tousLesPoints.length > 0 ? maxY - minY : 0;
    
    // Créer un profil normalisé (0-1) à partir des points RELATIFS à la boîte englobante
    const profil: Point2D[] = [];
    if (tousLesPoints.length > 0) {
      // Normaliser les points entre 0 et 1 en tenant compte de la position réelle du dessin
      tousLesPoints.forEach(point => {
        profil.push({
          x: largeur > 0 ? (point.x - minX) / largeur : 0,
          y: hauteur > 0 ? (point.y - minY) / hauteur : 0
        });
      });
    }

    return {
      id: `profil_${Date.now()}`,
      nom: `Profil personnalisé ${new Date().toLocaleTimeString()}`,
      largeur, // Dimensions réelles du dessin seulement
      hauteur, // Dimensions réelles du dessin seulement
      lignes: [...this.lignesCrayon],
      lignesDroites: [...this.lignesDroites],
      courbes: [...this.courbesBezier],
      profil
    };
  }

  /**
   * Efface tout le contenu
   */
  public effacer(): void {
    this.lignesCrayon = [];
    this.lignesDroites = [];
    this.courbesBezier = [];
    this.annulerSelection();
    
    this.redessiner();
    this.sauvegarderEtat();
    this.notifierChangement();
    this.emettrEvenement({ type: 'efface' });
  }

  /**
   * Annule la dernière action
   */
  public annuler(): boolean {
    if (this.indexHistorique > 0) {
      this.indexHistorique--;
      this.restaurerEtat(this.historique[this.indexHistorique]);
      this.emettrEvenement({ type: 'annule' });
      return true;
    }
    return false;
  }

  /**
   * Refait la dernière action annulée
   */
  public refaire(): boolean {
    if (this.indexHistorique < this.historique.length - 1) {
      this.indexHistorique++;
      this.restaurerEtat(this.historique[this.indexHistorique]);
      this.emettrEvenement({ type: 'refait' });
      return true;
    }
    return false;
  }

  /**
   * Ajoute les écouteurs d'événements
   */
  private ajouterEcouteurs(): void {
    if (!this.canvas) return;
    this.canvas.addEventListener('keydown', (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        this.supprimerSelection();
      }
    });
    this.canvas.tabIndex = 0;
    if (!this.canvas) return;
    
    this.canvas.addEventListener('mousedown', this.gererMouseDown.bind(this));
    this.canvas.addEventListener('mousemove', this.gererMouseMove.bind(this));
    this.canvas.addEventListener('mouseup', this.gererMouseUp.bind(this));
    this.canvas.addEventListener('mouseleave', this.gererMouseLeave.bind(this));
    
    // Empêcher le menu contextuel
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /**
   * Gère l'événement mousedown
   */
  private gererMouseDown(event: MouseEvent): void {
    const point = this.obtenirPointSouris(event);
    
    switch (this.outilActuel) {
      case OutilDessin.CRAYON:
        this.commencerDessinCrayon(point);
        break;
        
      case OutilDessin.LIGNE:
        this.commencerLigneDroite(point);
        break;
        
      case OutilDessin.BEZIER:
        this.gererClicBezier(point);
        break;
        
      case OutilDessin.SELECTION:
        this.commencerSelection(point);
        break;
    }
  }

  /**
   * Gère l'événement mousemove
   */
  private gererMouseMove(event: MouseEvent): void {
    const point = this.obtenirPointSouris(event);
    
    if (!this.estEnTrain) return;
    
    switch (this.outilActuel) {
      case OutilDessin.CRAYON:
        this.continuerDessinCrayon(point);
        break;
        
      case OutilDessin.LIGNE:
        this.mettreAJourLigneDroite(point);
        break;
        
      case OutilDessin.BEZIER:
        this.mettreAJourBezier(point);
        break;
        
      case OutilDessin.SELECTION:
        this.mettreAJourSelection(point);
        break;
    }
  }

  /**
   * Gère l'événement mouseup
   */
  private gererMouseUp(event: MouseEvent): void {
    const point = this.obtenirPointSouris(event);
    
    switch (this.outilActuel) {
      case OutilDessin.CRAYON:
        this.terminerDessinCrayon();
        break;
        
      case OutilDessin.LIGNE:
        this.terminerLigneDroite(point);
        break;
        
      case OutilDessin.BEZIER:
        // Géré dans gererClicBezier
        break;
        
      case OutilDessin.SELECTION:
        this.terminerSelection();
        break;
    }
  }

  /**
   * Gère l'événement mouseleave
   */
  private gererMouseLeave(): void {
    if (this.estEnTrain) {
      switch (this.outilActuel) {
        case OutilDessin.CRAYON:
          this.terminerDessinCrayon();
          break;
          
        case OutilDessin.LIGNE:
          this.annulerLigneDroite();
          break;
          
        case OutilDessin.SELECTION:
          this.terminerSelection();
          break;
      }
    }
  }

  /**
   * Obtient les coordonnées de la souris relatives au canvas
   */
  private obtenirPointSouris(event: MouseEvent): Point2D {
    if (!this.canvas) return { x: 0, y: 0 };
    
    const rect = this.canvas.getBoundingClientRect();
    let point = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    
    // Appliquer le magnétisme si activé
    if (this.configuration.magnetisme) {
      point = this.appliquerMagnetisme(point);
    }
    
    return point;
  }

  /**
   * Applique l'accroche magnétique à un point
   */
  private appliquerAccroche(point: Point2D): Point2D {
    if (!this.accrocheActive) {
      return point;
    }
    
    const tailleGrille = 20; // Taille de la grille en pixels
    
    return {
      x: Math.round(point.x / tailleGrille) * tailleGrille,
      y: Math.round(point.y / tailleGrille) * tailleGrille
    };
  }

  /**
   * Applique le magnétisme à la grille
   */
  private appliquerMagnetisme(point: Point2D): Point2D {
    return this.appliquerAccroche(point);
  }

  /**
   * Commence le dessin au crayon
   */
  private commencerDessinCrayon(point: Point2D): void {
    this.estEnTrain = true;
    this.pointsTemporaires = [point];
    this.emettrEvenement({ type: 'dessin_commence', outil: OutilDessin.CRAYON, point });
  }

  /**
   * Continue le dessin au crayon
   */
  private continuerDessinCrayon(point: Point2D): void {
    this.pointsTemporaires.push(point);
    this.redessiner();
    this.dessinerLigneTemporaire(this.pointsTemporaires);
  }

  /**
   * Termine le dessin au crayon
   */
  private terminerDessinCrayon(): void {
    if (this.pointsTemporaires.length > 1) {
      this.lignesCrayon.push([...this.pointsTemporaires]);
      this.sauvegarderEtat();
      this.notifierChangement();
      this.emettrEvenement({ type: 'dessin_termine', outil: OutilDessin.CRAYON });
    }
    
    this.estEnTrain = false;
    this.pointsTemporaires = [];
    this.redessiner();
  }

  /**
   * Commence une ligne droite
   */
  private commencerLigneDroite(point: Point2D): void {
    this.estEnTrain = true;
    this.pointDebut = point;
    this.emettrEvenement({ type: 'dessin_commence', outil: OutilDessin.LIGNE, point });
  }

  /**
   * Met à jour la ligne droite
   */
  private mettreAJourLigneDroite(point: Point2D): void {
    if (!this.pointDebut) return;
    
    this.redessiner();
    this.dessinerLigneTemporaire([this.pointDebut, point]);
  }

  /**
   * Termine la ligne droite
   */
  private terminerLigneDroite(point: Point2D): void {
    if (this.pointDebut) {
      const ligne: LigneDroite = {
        debut: this.pointDebut,
        fin: point
      };
      
      this.lignesDroites.push(ligne);
      this.sauvegarderEtat();
      this.notifierChangement();
      this.emettrEvenement({ type: 'dessin_termine', outil: OutilDessin.LIGNE });
    }
    
    this.estEnTrain = false;
    this.pointDebut = null;
    this.redessiner();
  }

  /**
   * Annule la ligne droite en cours
   */
  private annulerLigneDroite(): void {
    this.estEnTrain = false;
    this.pointDebut = null;
    this.redessiner();
  }

  /**
   * Gère les clics pour les courbes de Bézier
   */
  private gererClicBezier(point: Point2D): void {
    // Implémentation simplifiée - courbe de Bézier quadratique
    if (!this.pointsTemporaires.length) {
      // Premier point
      this.pointsTemporaires = [point];
      this.estEnTrain = true;
      this.emettrEvenement({ type: 'dessin_commence', outil: OutilDessin.BEZIER, point });
    } else if (this.pointsTemporaires.length === 1) {
      // Point de contrôle
      this.pointsTemporaires.push(point);
    } else if (this.pointsTemporaires.length === 2) {
      // Point final
      this.pointsTemporaires.push(point);
      
      const courbe: CourbeBezier = {
        debut: this.pointsTemporaires[0],
        controle1: this.pointsTemporaires[1],
        controle2: this.pointsTemporaires[1], // Courbe quadratique
        fin: this.pointsTemporaires[2]
      };
      
      this.courbesBezier.push(courbe);
      this.sauvegarderEtat();
      this.notifierChangement();
      this.emettrEvenement({ type: 'dessin_termine', outil: OutilDessin.BEZIER });
      
      this.estEnTrain = false;
      this.pointsTemporaires = [];
      this.redessiner();
    }
  }

  /**
   * Met à jour la courbe de Bézier en cours
   */
  private mettreAJourBezier(point: Point2D): void {
    if (this.pointsTemporaires.length === 1) {
      this.redessiner();
      this.dessinerLigneTemporaire([this.pointsTemporaires[0], point]);
    } else if (this.pointsTemporaires.length === 2) {
      this.redessiner();
      this.dessinerCourbeTemporaire(this.pointsTemporaires[0], this.pointsTemporaires[1], point);
    }
  }

  /**
   * Commence la sélection
   */
  private commencerSelection(point: Point2D): void {
    // Vérifier si on clique sur un élément existant
    const elementSelectionne = this.trouverElementAuPoint(point);
    
    if (elementSelectionne) {
      this.etatSelection = {
        actif: true,
        element: elementSelectionne,
        pointDebut: point
      };
    } else {
      this.annulerSelection();
    }
    
    this.estEnTrain = true;
    this.redessiner();
  }

  /**
   * Met à jour la sélection
   */
  private mettreAJourSelection(point: Point2D): void {
    if (this.etatSelection.actif && this.etatSelection.element && this.etatSelection.pointDebut) {
      // Calculer le déplacement
      const deplacement = {
        x: point.x - this.etatSelection.pointDebut.x,
        y: point.y - this.etatSelection.pointDebut.y
      };
      
      // Appliquer le déplacement (implémentation simplifiée)
      this.redessiner();
      this.dessinerElementSelectionne(this.etatSelection.element, deplacement);
    }
  }

  /**
   * Termine la sélection
   */
  private terminerSelection(): void {
    if (this.etatSelection.actif && this.etatSelection.element && this.etatSelection.pointDebut) {
      // Appliquer définitivement le déplacement
      // Implémentation simplifiée - à compléter selon les besoins
      this.sauvegarderEtat();
      this.notifierChangement();
    }
    
    this.estEnTrain = false;
    this.redessiner();
  }

  /**
   * Annule la sélection
   */
  private annulerSelection(): void {
    this.etatSelection = { actif: false };
    this.redessiner();
  }

  /**
   * Trouve un élément au point donné
   */
  private trouverElementAuPoint(point: Point2D): any {
    const tolerance = this.configuration.toleranceSelection;
    
    // Vérifier les lignes de crayon
    for (let i = 0; i < this.lignesCrayon.length; i++) {
      const ligne = this.lignesCrayon[i];
      if (this.pointProcheDeLigne(point, ligne, tolerance)) {
        return { type: 'crayon', index: i };
      }
    }
    
    // Vérifier les lignes droites
    for (let i = 0; i < this.lignesDroites.length; i++) {
      const ligne = this.lignesDroites[i];
      if (this.pointProcheDeLigneDroite(point, ligne, tolerance)) {
        return { type: 'ligne', index: i };
      }
    }
    
    // Vérifier les courbes de Bézier
    for (let i = 0; i < this.courbesBezier.length; i++) {
      const courbe = this.courbesBezier[i];
      if (this.pointProcheDeCourbe(point, courbe, tolerance)) {
        return { type: 'courbe', index: i };
      }
    }
    
    return null;
  }

  /**
   * Vérifie si un point est proche d'une ligne
   */
  private pointProcheDeLigne(point: Point2D, ligne: Point2D[], tolerance: number): boolean {
    for (let i = 0; i < ligne.length - 1; i++) {
      if (this.distancePointSegment(point, ligne[i], ligne[i + 1]) <= tolerance) {
        return true;
      }
    }
    return false;
  }

  /**
   * Vérifie si un point est proche d'une ligne droite
   */
  private pointProcheDeLigneDroite(point: Point2D, ligne: LigneDroite, tolerance: number): boolean {
    return this.distancePointSegment(point, ligne.debut, ligne.fin) <= tolerance;
  }

  /**
   * Vérifie si un point est proche d'une courbe de Bézier
   */
  private pointProcheDeCourbe(point: Point2D, courbe: CourbeBezier, tolerance: number): boolean {
    // Approximation par segments
    const segments = 20;
    for (let i = 0; i < segments; i++) {
      const t1 = i / segments;
      const t2 = (i + 1) / segments;
      const p1 = this.calculerPointBezier(t1, courbe);
      const p2 = this.calculerPointBezier(t2, courbe);
      
      if (this.distancePointSegment(point, p1, p2) <= tolerance) {
        return true;
      }
    }
    return false;
  }

  /**
   * Calcule la distance d'un point à un segment
   */
  private distancePointSegment(point: Point2D, debut: Point2D, fin: Point2D): number {
    const A = point.x - debut.x;
    const B = point.y - debut.y;
    const C = fin.x - debut.x;
    const D = fin.y - debut.y;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    
    if (lenSq === 0) {
      return Math.sqrt(A * A + B * B);
    }
    
    let param = dot / lenSq;
    
    if (param < 0) {
      param = 0;
    } else if (param > 1) {
      param = 1;
    }
    
    const xx = debut.x + param * C;
    const yy = debut.y + param * D;
    
    const dx = point.x - xx;
    const dy = point.y - yy;
    
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Calcule un point sur une courbe de Bézier
   */
  private calculerPointBezier(t: number, courbe: CourbeBezier): Point2D {
    const u = 1 - t;
    const tt = t * t;
    const uu = u * u;
    const uuu = uu * u;
    const ttt = tt * t;
    
    return {
      x: uuu * courbe.debut.x + 3 * uu * t * courbe.controle1.x + 3 * u * tt * courbe.controle2.x + ttt * courbe.fin.x,
      y: uuu * courbe.debut.y + 3 * uu * t * courbe.controle1.y + 3 * u * tt * courbe.controle2.y + ttt * courbe.fin.y
    };
  }

  /**
   * Redessine tout le canvas
   */
  private redessiner(): void {
    if (!this.contexte || !this.canvas) return;
    const { width, height } = this.canvas;
    this.contexte.clearRect(0, 0, width, height);
    this.contexte.fillStyle = '#ffffff';
    this.contexte.fillRect(0, 0, width, height);
    if (this.showGrid) {
      this.dessinerGrille();
    }
    if (!this.contexte) return;

    this.contexte.fillStyle = this.configuration.couleurFond;
    this.contexte.fillRect(0, 0, this.configuration.largeur, this.configuration.hauteur);

    if (this.configuration.afficherGrille) {
      this.dessinerGrille();
    }

    this.dessinerLignesCrayon();
    this.dessinerLignesDroites();
    this.dessinerCourbesBezier();

    this.dessinerSelection();
  }

  /**
   * Dessine la grille
   */
  private dessinerGrille(): void {
    if (!this.contexte) return;
    
    const tailleGrille = 20;
    
    this.contexte.strokeStyle = this.configuration.couleurGrille;
    this.contexte.lineWidth = 1;
    this.contexte.setLineDash([2, 2]);
    
    // Lignes verticales
    for (let x = 0; x <= this.configuration.largeur; x += tailleGrille) {
      this.contexte.beginPath();
      this.contexte.moveTo(x, 0);
      this.contexte.lineTo(x, this.configuration.hauteur);
      this.contexte.stroke();
    }
    
    // Lignes horizontales
    for (let y = 0; y <= this.configuration.hauteur; y += tailleGrille) {
      this.contexte.beginPath();
      this.contexte.moveTo(0, y);
      this.contexte.lineTo(this.configuration.largeur, y);
      this.contexte.stroke();
    }
    
    this.contexte.setLineDash([]);
  }

  /**
   * Dessine les lignes de crayon
   */
  private dessinerLignesCrayon(): void {
    if (!this.contexte) return;
    
    this.contexte.strokeStyle = this.configuration.couleurDessin;
    this.contexte.lineWidth = this.configuration.epaisseurLigne;
    this.contexte.lineCap = 'round';
    this.contexte.lineJoin = 'round';
    
    this.lignesCrayon.forEach(ligne => {
      if (ligne.length > 1) {
        this.contexte!.beginPath();
        this.contexte!.moveTo(ligne[0].x, ligne[0].y);
        
        for (let i = 1; i < ligne.length; i++) {
          this.contexte!.lineTo(ligne[i].x, ligne[i].y);
        }
        
        this.contexte!.stroke();
      }
    });
  }

  /**
   * Dessine les lignes droites
   */
  private dessinerLignesDroites(): void {
    if (!this.contexte) return;
    
    this.contexte.strokeStyle = this.configuration.couleurDessin;
    this.contexte.lineWidth = this.configuration.epaisseurLigne;
    this.contexte.lineCap = 'round';
    
    this.lignesDroites.forEach(ligne => {
      this.contexte!.beginPath();
      this.contexte!.moveTo(ligne.debut.x, ligne.debut.y);
      this.contexte!.lineTo(ligne.fin.x, ligne.fin.y);
      this.contexte!.stroke();
    });
  }

  /**
   * Dessine les courbes de Bézier
   */
  private dessinerCourbesBezier(): void {
    if (!this.contexte) return;
    
    this.contexte.strokeStyle = this.configuration.couleurDessin;
    this.contexte.lineWidth = this.configuration.epaisseurLigne;
    this.contexte.lineCap = 'round';
    
    this.courbesBezier.forEach(courbe => {
      this.contexte!.beginPath();
      this.contexte!.moveTo(courbe.debut.x, courbe.debut.y);
      this.contexte!.bezierCurveTo(
        courbe.controle1.x, courbe.controle1.y,
        courbe.controle2.x, courbe.controle2.y,
        courbe.fin.x, courbe.fin.y
      );
      this.contexte!.stroke();
    });
  }

  /**
   * Dessine une ligne temporaire
   */
  private dessinerLigneTemporaire(points: Point2D[]): void {
    if (!this.contexte || points.length < 2) return;
    
    this.contexte.strokeStyle = this.configuration.couleurDessin;
    this.contexte.lineWidth = this.configuration.epaisseurLigne;
    this.contexte.lineCap = 'round';
    this.contexte.lineJoin = 'round';
    this.contexte.globalAlpha = 0.7;
    
    this.contexte.beginPath();
    this.contexte.moveTo(points[0].x, points[0].y);
    
    for (let i = 1; i < points.length; i++) {
      this.contexte.lineTo(points[i].x, points[i].y);
    }
    
    this.contexte.stroke();
    this.contexte.globalAlpha = 1;
  }

  /**
   * Dessine une courbe temporaire
   */
  private dessinerCourbeTemporaire(debut: Point2D, controle: Point2D, fin: Point2D): void {
    if (!this.contexte) return;
    
    this.contexte.strokeStyle = this.configuration.couleurDessin;
    this.contexte.lineWidth = this.configuration.epaisseurLigne;
    this.contexte.lineCap = 'round';
    this.contexte.globalAlpha = 0.7;
    
    this.contexte.beginPath();
    this.contexte.moveTo(debut.x, debut.y);
    this.contexte.quadraticCurveTo(controle.x, controle.y, fin.x, fin.y);
    this.contexte.stroke();
    
    this.contexte.globalAlpha = 1;
  }

  /**
   * Dessine la sélection
   */
  private dessinerSelection(): void {
    if (!this.contexte || !this.etatSelection.actif || !this.etatSelection.element) return;
    
    // Dessiner un contour de sélection
    this.contexte.strokeStyle = this.configuration.couleurSelection;
    this.contexte.lineWidth = 2;
    this.contexte.setLineDash([5, 5]);
    
    // Implémentation simplifiée - à compléter selon les besoins
    
    this.contexte.setLineDash([]);
  }

  /**
   * Dessine un élément sélectionné avec déplacement
   */
  private dessinerElementSelectionne(element: any, deplacement: Point2D): void {
    // Implémentation simplifiée - à compléter selon les besoins
  }

  /**
   * Sauvegarde l'état actuel dans l'historique
   */
  private sauvegarderEtat(): void {
    const etat = this.obtenirProfil();
    
    // Supprimer les états futurs si on est au milieu de l'historique
    if (this.indexHistorique < this.historique.length - 1) {
      this.historique = this.historique.slice(0, this.indexHistorique + 1);
    }
    
    this.historique.push(etat);
    this.indexHistorique = this.historique.length - 1;
    
    // Limiter la taille de l'historique
    const tailleMaxHistorique = 50;
    if (this.historique.length > tailleMaxHistorique) {
      this.historique = this.historique.slice(-tailleMaxHistorique);
      this.indexHistorique = this.historique.length - 1;
    }
  }

  /**
   * Restaure un état depuis l'historique
   */
  private restaurerEtat(etat: ProfilBaleinePersonnalise): void {
    this.lignesCrayon = [...etat.lignes];
    this.lignesDroites = [...etat.lignesDroites];
    this.courbesBezier = [...etat.courbes];
    
    this.redessiner();
    this.notifierChangement();
  }

  /**
   * Notifie les changements
   */
  private notifierChangement(): void {
    if (this.onChangement) {
      this.onChangement(this.obtenirProfil());
    }
  }

  /**
   * Émet un événement
   */
  private emettrEvenement(evenement: EvenementCanvas): void {
    if (this.onEvenement) {
      this.onEvenement(evenement);
    }
  }

  /**
   * Nettoie les ressources
   */
  public dispose(): void {
    if (this.canvas) {
      this.canvas.removeEventListener('mousedown', this.gererMouseDown.bind(this));
      this.canvas.removeEventListener('mousemove', this.gererMouseMove.bind(this));
      this.canvas.removeEventListener('mouseup', this.gererMouseUp.bind(this));
      this.canvas.removeEventListener('mouseleave', this.gererMouseLeave.bind(this));
    }
    
    this.canvas = null;
    this.contexte = null;
    this.onChangement = undefined;
    this.onEvenement = undefined;
    this.historique = [];
  }
}